import { useState, useCallback } from 'react'
import {
  Type,
  Calendar,
  Hash,
  ToggleLeft,
  List,
  Image,
  Link2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface TemplateVariable {
  id: string
  name: string
  displayName: string
  description?: string
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'image' | 'link' | 'richtext'
  required: boolean
  defaultValue?: string
  options?: string[]
  validation?: {
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
  }
  category?: string
  aiSuggestionEnabled?: boolean
}

export interface VariableEditorProps {
  variables: TemplateVariable[]
  values: Record<string, string | number | boolean>
  errors?: Record<string, string>
  onChange: (name: string, value: string | number | boolean) => void
  onAISuggest?: (variableName: string) => Promise<string>
  isGeneratingAI?: boolean
  className?: string
}

const variableTypeIcons: Record<TemplateVariable['type'], React.ComponentType<{ className?: string }>> = {
  text: Type,
  number: Hash,
  date: Calendar,
  boolean: ToggleLeft,
  select: List,
  image: Image,
  link: Link2,
  richtext: Type,
}

interface VariableFieldProps {
  variable: TemplateVariable
  value: string | number | boolean | undefined
  error?: string
  onChange: (value: string | number | boolean) => void
  onAISuggest?: () => Promise<string>
  isGeneratingAI?: boolean
}

function VariableField({ variable, value, error, onChange, onAISuggest, isGeneratingAI }: VariableFieldProps) {
  const [isAISuggesting, setIsAISuggesting] = useState(false)

  const handleAISuggest = async () => {
    if (!onAISuggest) return
    setIsAISuggesting(true)
    try {
      const suggestion = await onAISuggest()
      onChange(suggestion)
    } finally {
      setIsAISuggesting(false)
    }
  }

  const Icon = variableTypeIcons[variable.type]

  const renderInput = () => {
    switch (variable.type) {
      case 'boolean':
        return (
          <div className="flex items-center gap-3">
            <Switch
              checked={Boolean(value)}
              onCheckedChange={(checked) => onChange(checked)}
              id={variable.id}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {value ? 'Yes' : 'No'}
            </span>
          </div>
        )

      case 'select':
        return (
          <Select
            value={String(value || '')}
            onValueChange={(v) => onChange(v)}
          >
            <SelectTrigger className={cn('rounded-xl', error && 'border-red-500')}>
              <SelectValue placeholder={`Select ${variable.displayName.toLowerCase()}...`} />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {variable.options?.map((option) => (
                <SelectItem key={option} value={option} className="rounded-lg">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'date':
        return (
          <Input
            type="date"
            id={variable.id}
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            className={cn('rounded-xl', error && 'border-red-500')}
          />
        )

      case 'number':
        return (
          <Input
            type="number"
            id={variable.id}
            value={value !== undefined ? String(value) : ''}
            onChange={(e) => onChange(e.target.valueAsNumber || 0)}
            min={variable.validation?.min}
            max={variable.validation?.max}
            className={cn('rounded-xl', error && 'border-red-500')}
            placeholder={variable.defaultValue || `Enter ${variable.displayName.toLowerCase()}`}
          />
        )

      case 'richtext':
        return (
          <Textarea
            id={variable.id}
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            maxLength={variable.validation?.maxLength}
            className={cn('rounded-xl resize-none', error && 'border-red-500')}
            placeholder={variable.defaultValue || `Enter ${variable.displayName.toLowerCase()}...`}
          />
        )

      case 'image':
      case 'link':
        return (
          <Input
            type={variable.type === 'link' ? 'url' : 'text'}
            id={variable.id}
            value={String(value || '')}
            onChange={(e) => onChange(e.target.value)}
            className={cn('rounded-xl', error && 'border-red-500')}
            placeholder={
              variable.type === 'link'
                ? 'https://example.com'
                : 'Enter image URL or select from assets'
            }
          />
        )

      default:
        return (
          <div className="relative">
            <Input
              type="text"
              id={variable.id}
              value={String(value || '')}
              onChange={(e) => onChange(e.target.value)}
              minLength={variable.validation?.minLength}
              maxLength={variable.validation?.maxLength}
              pattern={variable.validation?.pattern}
              className={cn('rounded-xl pr-10', error && 'border-red-500')}
              placeholder={variable.defaultValue || `Enter ${variable.displayName.toLowerCase()}`}
            />
            {variable.aiSuggestionEnabled && onAISuggest && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAISuggest}
                disabled={isAISuggesting || isGeneratingAI}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 rounded-lg"
                title="Get AI suggestion"
              >
                <Sparkles className={cn('h-4 w-4', isAISuggesting && 'animate-pulse text-purple-500')} />
              </Button>
            )}
          </div>
        )
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={variable.id} className="text-sm font-medium text-gray-700 dark:text-gray-300">
          <Icon className="h-3.5 w-3.5 inline mr-1.5 text-gray-400" />
          {variable.displayName}
          {variable.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
      {variable.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{variable.description}</p>
      )}
      {renderInput()}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-500">
          <AlertCircle className="h-3 w-3" />
          {error}
        </div>
      )}
      {variable.validation?.maxLength && variable.type === 'richtext' && (
        <p className="text-xs text-gray-400 text-right">
          {String(value || '').length} / {variable.validation.maxLength}
        </p>
      )}
    </div>
  )
}

export function VariableEditor({
  variables,
  values,
  errors = {},
  onChange,
  onAISuggest,
  isGeneratingAI,
  className,
}: VariableEditorProps) {
  // Group variables by category
  const groupedVariables = variables.reduce<Record<string, TemplateVariable[]>>((acc, variable) => {
    const category = variable.category || 'General'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(variable)
    return acc
  }, {})

  const categories = Object.keys(groupedVariables)
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() =>
    categories.reduce<Record<string, boolean>>((acc, cat) => ({ ...acc, [cat]: true }), {})
  )

  const toggleCategory = useCallback((category: string) => {
    setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }))
  }, [])

  const requiredCount = variables.filter((v) => v.required).length
  const filledRequiredCount = variables.filter(
    (v) => v.required && values[v.name] !== undefined && values[v.name] !== ''
  ).length

  if (variables.length === 0) {
    return (
      <div className={cn('p-6 text-center', className)}>
        <Type className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This template has no variables to fill in.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Progress indicator */}
      <div className="flex items-center justify-between px-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {variables.length} variable{variables.length !== 1 ? 's' : ''}
        </span>
        {requiredCount > 0 && (
          <Badge
            className={cn(
              'rounded-full text-xs',
              filledRequiredCount === requiredCount
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            )}
          >
            {filledRequiredCount}/{requiredCount} required filled
          </Badge>
        )}
      </div>

      {/* Variable groups */}
      {categories.length === 1 && categories[0] === 'General' ? (
        // Single category - no collapsible
        <div className="space-y-4">
          {(groupedVariables['General'] ?? []).map((variable) => (
            <VariableField
              key={variable.id}
              variable={variable}
              value={values[variable.name]}
              error={errors[variable.name]}
              onChange={(value) => onChange(variable.name, value)}
              onAISuggest={
                variable.aiSuggestionEnabled && onAISuggest
                  ? () => onAISuggest(variable.name)
                  : undefined
              }
              isGeneratingAI={isGeneratingAI}
            />
          ))}
        </div>
      ) : (
        // Multiple categories - use collapsible
        <div className="space-y-3">
          {categories.map((category) => (
            <Collapsible
              key={category}
              open={openCategories[category]}
              onOpenChange={() => toggleCategory(category)}
              className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {category}
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="rounded-full text-xs">
                    {(groupedVariables[category] ?? []).length}
                  </Badge>
                  {openCategories[category] ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 space-y-4 bg-white dark:bg-gray-800/30">
                  {(groupedVariables[category] ?? []).map((variable) => (
                    <VariableField
                      key={variable.id}
                      variable={variable}
                      value={values[variable.name]}
                      error={errors[variable.name]}
                      onChange={(value) => onChange(variable.name, value)}
                      onAISuggest={
                        variable.aiSuggestionEnabled && onAISuggest
                          ? () => onAISuggest(variable.name)
                          : undefined
                      }
                      isGeneratingAI={isGeneratingAI}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  )
}
